import { useTranslation } from "react-i18next";
import useMediaByValue from "hooks/useMediaByValue";
import { Box, Typography } from "@mui/material";
import CustomButton from "components/Button";
import { useNavigate } from "react-router-dom";

interface IAdminGlobalNotificationPreviewProps {
  description: string;
  background: string;
  previewImage: File | string;
  title: string;
  isButtonVisible: boolean;
  buttonText: string;
  buttonLink: string;
}

const GlobalNotificationPreview = ({
  description,
  background,
  title,
  isButtonVisible,
  buttonText,
  buttonLink,
  previewImage,
}: IAdminGlobalNotificationPreviewProps) => {
  const { t } = useTranslation();
  const isMediumViewPort = useMediaByValue("(min-width:600px)");
  const navigate = useNavigate();

  const handleCustomButtonClick = () => {
    if (!buttonLink) {
      return;
    }

    try {
      const url = new URL(buttonLink);

      if (url.origin === window.location.origin) {
        navigate(url.pathname + url.search + url.hash);
      } else {
        window.open(url.toString(), "_blank", "noopener noreferrer");
      }
    } catch {
      navigate(buttonLink);
    }
  };

  return (
    <>
      <Typography
        sx={{
          fontFamily: "var(--cygreMedium), sans-serif",
          color: "#2E263D",
          fontSize: "16px",
          lineHeight: 1.22,
          textAlign: "center",
          marginBottom: "10px",
        }}
      >
        {t("notifiPreviewAndCustomization")}
      </Typography>
      <Box
        sx={{
          display: "flex",
          width: "100%",
          maxWidth: "800px",
          maxHeight: "90dvh",
          margin: "32px auto 0px auto",
          borderRadius: "8px",
          bgcolor: { background },
          boxShadow: "0 0 4px 0 rgba(165, 163, 174, 0.3);",
          overflowY: "auto",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            padding: "64px 32px 48px 32px",
            flex: isMediumViewPort ? "0 0 60%" : "0 0 100%",
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              marginBottom: "16px",
              fontFamily: "var(--cygreMedium), sans-serif",
              fontWeight: 500,
              fontSize: "32px",
              lineHeight: 1.32,
              color: background === "#111" ? "#FFFFFF" : "#2E263D",
            }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              marginBottom: "24px",
              fontFamily: "var(--cygreMedium), sans-serif",
              fontWeight: 400,
              fontSize: "16px",
              lineHeight: 1.42,
              color: background === "#111" ? "#FFFFFF" : "#2E263D",
            }}
          >
            {description}
          </Typography>
          {isButtonVisible && (
            <CustomButton
              width={"max-content"}
              height={50}
              textButton={buttonText}
              customBgColor="#F4701B"
              customHoverColor="#f0670b"
              customBorderRadius="8px"
              customFontSize="16px"
              customPadding="16px 24px"
              customTextTransform="none"
              onClick={handleCustomButtonClick}
            />
          )}
        </Box>
        {isMediumViewPort && (
          <Box
            sx={{
              flex: "0 0 40%",
              margin: "10px 0px 10px 0px",
              backgroundSize: "contain",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundImage: `url(${typeof previewImage === "string" ? previewImage : URL.createObjectURL(previewImage)})`,
            }}
          />
        )}
      </Box>
    </>
  );
};

export default GlobalNotificationPreview;
